import { useEffect, useRef } from "react";
import { Document } from "flexsearch";
import { useDepartmentFilterState } from "../components/filters/items/departments-filter-item";
import { usePositionFilterState } from "../components/filters/items/positions-filter-item";
import { useOfficeFilterState } from "../components/filters/items/office-locations-filter-item";
import { useEmploymentFilterState } from "../components/filters/items/employment-filter-item";
import { intersectionBy } from "lodash";
import { ClientJobCardListItem } from "../types/client-job-card-list-item";

const EMPTY_ARRAY = [];

// Create a search index for each property we care about.
// since they're simple strings, we can just treat it like a text search
const useCareersPageSearch = <T extends ClientJobCardListItem>(
  options: T[],
) => {
  const departments = useRef(
    new Document({
      document: {
        id: "id",
        index: ["departments[]:departmentId"],
        store: true,
      },
    }),
  );

  const positions = useRef(
    new Document({
      document: {
        id: "id",
        index: ["positionTypes[]:positionTypeId"],
        store: true,
      },
    }),
  );

  const employmentTypes = useRef(
    new Document({
      document: {
        id: "id",
        index: ["jobEmploymentTypes[]:employmentTypeId"],
        store: true,
      },
    }),
  );

  const offices = useRef(
    new Document({
      document: {
        id: "id",
        index: ["jobOffices[]:name"],
        store: true,
      },
    }),
  );

  useEffect(() => {
    // Add the items to their search index
    options.map((o) => {
      departments.current.add(o.id, o);
      positions.current.add(o.id, o);
      employmentTypes.current.add(o.id, o);
      offices.current.add(o.id, o);
    });
  }, [options]);

  return {
    departments: departments.current,
    positions: positions.current,
    employmentTypes: employmentTypes.current,
    offices: offices.current,
  };
};

const resultToItem = (res) => {
  return res.result.map((item) => {
    return item.doc;
  });
};

// Map over the results and return the intersection of the results
// or if no filters are applied return all the results
export const useFilteredJobs = (jobs: {
  data: ClientJobCardListItem[];
  isLoading?: boolean;
}) => {
  const departmentFilters = useDepartmentFilterState();
  const search = useCareersPageSearch(jobs.data ?? []);
  const departmentFilterKeys = Object.keys(departmentFilters.selected);

  const departmentResults = new Map();
  departmentFilterKeys.forEach((id) => {
    search.departments
      .search(id, { enrich: true, limit: 30000 })
      .map(resultToItem)
      .forEach((item) => {
        item.forEach((i) => {
          departmentResults.set(i.id, i);
        });
      });
  });

  const positionFilters = usePositionFilterState();
  const positionFilterKeys = Object.keys(positionFilters.selected);
  const positionResults = new Map();
  positionFilterKeys.forEach((id) => {
    search.positions
      .search(id, { enrich: true, limit: 30000 })
      .map(resultToItem)
      .forEach((item) => {
        item.forEach((i) => {
          positionResults.set(i.id, i);
        });
      });
  });

  const officeFilters = useOfficeFilterState();
  const officeFilterKeys = Object.keys(officeFilters.selected);
  const officeResults = new Map();

  officeFilterKeys.forEach((id) => {
    search.offices
      .search(id, { enrich: true, limit: 30000 })
      .map(resultToItem)
      .forEach((item) => {
        item.forEach((i) => {
          officeResults.set(i.id, i);
        });
      });
  });

  const employmentFilters = useEmploymentFilterState();
  const employmentFilterKeys = Object.keys(employmentFilters.selected);
  const employmentResults = new Map();
  employmentFilterKeys.forEach((id) => {
    search.employmentTypes
      .search(id, { enrich: true, limit: 30000 })
      .map(resultToItem)
      .forEach((item) => {
        item.forEach((i) => {
          employmentResults.set(i.id, i);
        });
      });
  });

  const areFiltersApplied = ![
    officeFilterKeys.length === 0,
    positionFilterKeys.length === 0,
    departmentFilterKeys.length === 0,
    employmentFilterKeys.length === 0,
  ].every(Boolean);

  const isEnabled = (f) => f.length > 0;

  const departments = Array.from(departmentResults.values());
  const positions = Array.from(positionResults.values());
  const offices = Array.from(officeResults.values());
  const employmentTypes = Array.from(employmentResults.values());

  const shouldApply = [
    isEnabled(departmentFilterKeys) && departments,
    isEnabled(positionFilterKeys) && positions,
    isEnabled(officeFilterKeys) && offices,
    isEnabled(employmentFilterKeys) && employmentTypes,
  ].filter(Boolean);

  if (shouldApply.length === 0 && !areFiltersApplied) {
    return {
      isFiltered: false,
      results: jobs.data ?? EMPTY_ARRAY,
      isLoading: jobs.isLoading,
    };
  }

  return {
    results: intersectionBy(...shouldApply, (s) => s.id),
    isFiltered: true,
    isLoading: jobs.isLoading,
  };
};
